<template>
    <!-- MBR047 Activity — member_app_UI/song_fa_members_activity_transaction_history_mobile -->
    <div class="page-header align-items-start min-vh-screen mbr-activity">
        <span class="mask bg-white"></span>
        <div class="container p-0">
            <header class="mbr-activity__bar">
                <div class="mbr-activity__head">
                    <h1 class="mbr-activity__title">{{ $t('activity.title') }}</h1>
                    <!-- MBR108 Receipt filters too (by store only); the dot counts only what applies to the tab -->
                    <FilterToolbarTransaction
                        :filterArrCashbackEarnStatus="isReceiptTab ? [] : filterArrCashbackEarnStatus"
                        :filterCashbackTimeSelected="isReceiptTab ? null : filterCashbackTimeSelected"
                        :filterArrStore="filterArrStore"
                        v-if="isFirstLoadDone && isFilterTab"
                    />
                </div>
                <!-- MBR048 same tabs and gating as before; active state follows typeSelected
                     instead of jQuery toggling .active on #nav_item_N -->
                <nav v-if="isFirstLoadDone" class="mbr-activity__segment" :aria-label="$t('activity.transaction_types')">
                    <button
                        v-for="tab in tabs"
                        :key="tab.type"
                        type="button"
                        class="mbr-activity__seg"
                        :class="{ 'mbr-activity__seg--active': typeSelected == tab.type }"
                        @click="changeType(tab.type)"
                    >
                        {{ tab.label }}
                    </button>
                </nav>
            </header>
            <main v-if="isFirstLoadDone" class="mbr-activity__body">
                <!-- MBR049 balance summary: same values and merchant flags as before -->
                <section v-if="[1, 11].includes(typeSelected)" class="mbr-activity__summary">
                    <span class="mbr-activity__ring mbr-activity__ring--a" aria-hidden="true"></span>
                    <span class="mbr-activity__ring mbr-activity__ring--b" aria-hidden="true"></span>
                    <div class="mbr-activity__sum-row">
                        <div class="mbr-activity__sum-main">
                            <span class="mbr-activity__eyebrow mbr-activity__eyebrow--peach">{{ typeSelected == 11 ? $t('activity.available') : $t('reward.wallet_balance') }}</span>
                            <div class="mbr-activity__sum-value">{{ typeSelected == 11 ? membershipPoint : membershipCashback }}</div>
                        </div>
                        <div class="mbr-activity__sum-side">
                            <span class="mbr-activity__eyebrow">{{ $t('activity.pending') }}</span>
                            <div class="mbr-activity__sum-small">{{ totalPendingEarnedAmount }}</div>
                            <template v-if="merchantMember && merchantMember.allow_show_redeemed_cashback != 0">
                                <span class="mbr-activity__eyebrow mbr-activity__eyebrow--gap">{{ $t('activity.redeemed') }}</span>
                                <div class="mbr-activity__sum-small">{{ totalCashbackUserd }}</div>
                            </template>
                        </div>
                    </div>
                    <div
                        v-if="merchantMember && merchantMember.allow_show_lifetime_earn_cashback != 0"
                        class="mbr-activity__lifetime"
                    >
                        <i18n-t keypath="activity.lifetime_earning" tag="span" scope="global">
                            <template #amount><strong>{{ totalLifetimeEarning }}</strong></template>
                        </i18n-t>
                    </div>
                    <ul
                        v-if="typeSelected == 1 && isLoadDone && (merchantMember.notice_transaction_amount_expiry || []).length"
                        class="mbr-activity__notices"
                    >
                        <li v-for="(text, index) in merchantMember.notice_transaction_amount_expiry" :key="index">
                            <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                            <!-- backend HTML; its inline red <span> is recoloured below -->
                            <span v-html="text"></span>
                        </li>
                    </ul>
                </section>
                <!-- MBR050 tier / donation cards: same gating and data -->
                <section
                    v-if="merchantMember && merchantMember.allow_setup_membership_tiering != 0 && merchantMember.allow_show_membership_spending != 0 && typeSelected == 2"
                    class="mbr-activity__card"
                >
                    <div class="mbr-activity__card-row">
                        <div class="mbr-activity__card-main">
                            <span class="mbr-activity__label">{{ $t('activity.total_spending') }}</span>
                            <div class="mbr-activity__card-value">{{ merchantMember.amount_spent }}</div>
                        </div>
                        <div class="mbr-activity__tier">
                            <img
                                v-if="merchantMember.tier_icon"
                                :src="merchantMember.tier_icon"
                                class="mbr-activity__tier-icon"
                                alt=""
                            />
                            <span v-if="merchantMember.current_tier_name" class="mbr-activity__tier-name">{{ merchantMember.current_tier_name }}</span>
                        </div>
                    </div>
                    <material-progress :percentage="merchantMember.level_percentage" variant="fill" class="mbr-activity__progress" />
                    <p v-if="merchantMember.level_note" class="mbr-activity__note">{{ merchantMember.level_note }}</p>
                </section>
                <section v-if="merchantMember && typeSelected == 3" class="mbr-activity__card">
                    <span class="mbr-activity__label">{{ $t('activity.total_donation') }}</span>
                    <div class="mbr-activity__card-value">{{ totalDonation }}</div>
                    <p class="mbr-activity__note">{{ $t('activity.donation_thanks') }}</p>
                </section>

                <!-- MBR051 month picker: same Datepicker + timeSelect watcher, drawn as the design's pill -->
                <div class="mbr-activity__month-row">
                    <!-- teleportCenter: anchored under the pill, the menu's Select button sat behind MbrBottomNav -->
                    <Datepicker
                        v-model="timeSelect"
                        monthPicker
                        :locale="dateLocale()"
                        teleportCenter
                        class="mbr-activity__picker"
                        menuClassName="mbr-dp-menu"
                        :format="format"
                        :selectText="$t('activity.select')"
                        :cancelText="$t('common.cancel')"
                    >
                        <template #trigger>
                            <button type="button" class="mbr-activity__month">
                                <span>{{ monthLabel }}</span>
                                <svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="m6 9 6 6 6-6" /></svg>
                            </button>
                        </template>
                    </Datepicker>
                    <span v-if="isLoadDone" class="mbr-activity__count">{{ transactionCount == 1 ? $t('activity.count_one', { count: transactionCount }) : $t('activity.count_other', { count: transactionCount }) }}</span>
                </div>

                <!-- MBR051 rows grouped by day (the API groups by month only) -->
                <section v-for="group in dayGroups" :key="group.key" class="mbr-activity__group">
                    <h2 class="mbr-activity__day">{{ group.label }}</h2>
                    <article v-for="(value, index2) in group.rows" :key="group.key + '_' + index2" class="mbr-activity__row">
                        <span class="mbr-activity__icon" :class="`mbr-activity__icon--${rowKind(value)}`" aria-hidden="true">
                            <svg v-if="rowKind(value) == 'in'" fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 19V5m-7 7 7-7 7 7" /></svg>
                            <svg v-else-if="rowKind(value) == 'voucher'" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                            <svg v-else fill="none" stroke="currentColor" stroke-width="2.2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m7-7-7 7-7-7" /></svg>
                        </span>
                        <div class="mbr-activity__row-main">
                            <div class="mbr-activity__row-title">{{ value.transaction_label ?? "" }}</div>
                            <div class="mbr-activity__row-time">{{ timeOf(value.createdAt) }}</div>
                            <div v-if="value.receipt_no" class="mbr-activity__row-meta">{{ $t('activity.receipt_no', { no: value.receipt_no }) }}</div>
                        </div>
                        <div
                            class="mbr-activity__amount"
                            :class="value.transaction_type_object.SIGN == '+' ? 'mbr-activity__amount--in' : 'mbr-activity__amount--out'"
                        >
                            <span v-if="value.payment_amount != 0 || value.amount_direct_deduct != 0">{{ value.transaction_type_object.SIGN == "-" ? "\u2212" : value.transaction_type_object.SIGN }}</span>{{ typeSelected == 1 && value.transaction_type == 2 ? value.amount_direct_deduct_format : value.payment_amount_format }}
                        </div>
                        <div
                            v-if="typeSelected == 1 && (value.earned_expiry_date || value.earned_pending_time)"
                            class="mbr-activity__row-earn"
                        >
                            <span>{{ value.earned_status == 1 ? $t('activity.valid_until', { date: value.earned_expiry_date }) : $t('activity.available_by', { date: value.earned_pending_time }) }}</span>
                            <span class="mbr-activity__status" :class="value.earned_status == 1 ? 'mbr-activity__status--ok' : 'mbr-activity__status--wait'">
                                {{ value.earned_status == 1 ? $t('activity.confirmed') : $t('activity.pending') }}
                            </span>
                        </div>
                    </article>
                </section>
                <p v-if="isLoadDone && !transactionCount" class="mbr-activity__empty">{{ $t('activity.no_item') }}</p>
            </main>
            <!-- MBR122 my-transactions failed: full-page before the first load, in place of the list after it
                 (the list was already cleared, so "No transactions" would be wrong) -->
            <div
                v-if="loadFailed"
                class="mbr-activity__error"
                :class="{ 'mbr-activity__error--page': !isFirstLoadDone }"
                role="alert"
            >
                <span class="mbr-activity__error-icon" aria-hidden="true">
                    <i class="material-icons-round">cloud_off</i>
                </span>
                <h2 class="mbr-activity__error-title">{{ $t('app.load_failed') }}</h2>
                <p class="mbr-activity__error-sub">{{ $t('legal.check_connection') }}</p>
                <material-button class="mbr-activity__error-retry" :disabled="loaderActive" @click="reloadTransactions()">{{ $t('legal.try_again') }}</material-button>
            </div>
        </div>
        <FilterTransaction
            :filterCashbackType="filterCashbackType"
            :filterArrCashbackEarnStatus="filterArrCashbackEarnStatus"
            :filterStore="filterStore"
            :filterArrStore="filterArrStore"
            :filterCashbackTimeSelected="filterCashbackTimeSelected"
            :filterCashbackTime="filterCashbackTime"
            :filterData="filterData"
            :resetData="resetData"
            :storesOnly="isReceiptTab"
        />
        <material-loading :active="loaderActive" />
        <div class="position-fixed top-1 end-1 z-index-2">
            <material-snackbar v-if="snackbar === 'danger' || snackbar === 'success'" title="" date=""
                :description="message" :icon="{ component: 'campaign', color: 'white' }" :color="snackbar"
                :close-handler="closeSnackbar" />
        </div>
        <QrcodeMember />
        <MbrBottomNav />
    </div>

</template>

<script>
import { mapMutations, mapActions } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialButton from "@/components/MaterialButton.vue";
import MemberService from "../services/member.service";
import 'bootstrap/dist/js/bootstrap.bundle'
import MaterialLoading from "@/components/MaterialLoading.vue";
import MaterialProgress from "@/components/MaterialProgress.vue";
import FilterTransaction from "@/components/FilterTransaction.vue";
import FilterToolbarTransaction from "@/components/FilterToolbarTransaction.vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import $ from "jquery";
import { getMerchantCode } from "@/config/utils";
import { isEnglish, formatDate as formatLocaleDate, dateLocale } from "@/i18n";
import QrcodeMember from "@/components/QrcodeMember.vue";
import MbrBottomNav from "@/components/MbrBottomNav.vue";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default {
    name: "my-transacions",
    components: {
        MaterialSnackbar,
        MaterialButton,
        MaterialLoading,
        MaterialProgress,
        FilterTransaction,
        FilterToolbarTransaction,
        Datepicker,
        QrcodeMember,
        MbrBottomNav,
    },
    data() {
        return {
            listTransaction: [],
            totalLifetimeEarning: "",
            membershipCashback: "",
            membershipPoint: "",
            totalPendingEarnedAmount: "",
            totalCashbackUserd: "",
            totalDonation: 0,
            currentTime: '',
            merchantCode: "",
            loaderActive: false,
            loadFailed: false,
            snackbar: null,
            message: "",
            typeSelected: 1,
            isLoadDone: false,
            isFirstLoadDone: false,
            filterArrCashbackEarnStatus: [],
            filterArrStore: [],
            filterCashbackTimeSelected: null,
            timeSelect: {
                year: (new Date()).getFullYear(),
                month: (new Date()).getMonth()
            },
            params: {
                filter_cashback_earn_status: [],
                filter_store: [],
                filter_cashback_time: null,
            },
            merchantMember: {},
            rewardPoint: 2,
            pointName: 'Point'
        };
    },
    // draws its own header like the design: hide the top navbar, and keep the
    // sidenav as an off-screen drawer (same as MyRewards / Store)
    beforeMount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.add("mbr-sidenav-overlay");
        app.classList.remove("g-sidenav-pinned");
    },
    beforeUnmount() {
        this.toggleNavbarOnly();
        this.toggleHideConfig();
        const app = document.querySelector("#app");
        app.classList.remove("mbr-sidenav-overlay");
        if (window.innerWidth > 1200) {
            app.classList.add("g-sidenav-pinned");
        }
    },
    async mounted() {
        const code = getMerchantCode();
        this.merchantCode = code ? `/${code}` : '';
        await this.getMyTransactions(this.typeSelected, this.params);
    },
    computed: {
        isReceiptTab() {
            return this.typeSelected == 2;
        },

        // tabs whose list honours the filter sheet
        isFilterTab() {
            return this.typeSelected == 1 || this.isReceiptTab;
        },

        tabs() {
            const member = this.merchantMember || {};
            return [
                { type: 1, label: this.$t("home.wallet"), show: true },
                { type: 11, label: this.pointName, show: member.reward_type == this.rewardPoint },
                { type: 2, label: this.$t("activity.receipt"), show: true },
                { type: 3, label: this.$t("activity.donation"), show: member.show_donation != 0 },
            ].filter((tab) => tab.show);
        },

        monthLabel() {
            const { year, month } = this.timeSelect || {};
            if (year === undefined || month === undefined) {
                return "";
            }
            if (!isEnglish()) {
                return formatLocaleDate(new Date(year, month, 1), { month: "long", year: "numeric" });
            }
            return `${MONTHS[month]} ${year}`;
        },

        // list_transaction is { "September, 2026": [rows] }; createdAt is "d/m/Y h:i A"
        dayGroups() {
            const groups = [];
            Object.keys(this.listTransaction || {}).forEach((monthKey) => {
                (this.listTransaction[monthKey] || []).forEach((row) => {
                    const date = String(row.createdAt || "").split(" ")[0];
                    let group = groups.find((item) => item.key === date);
                    if (!group) {
                        group = { key: date, label: this.dayLabel(date) || monthKey, rows: [] };
                        groups.push(group);
                    }
                    group.rows.push(row);
                });
            });
            return groups;
        },

        transactionCount() {
            return this.dayGroups.reduce((total, group) => total + group.rows.length, 0);
        },
    },
    methods: {
        dateLocale,
        ...mapMutations(["toggleEveryDisplay", "toggleHideConfig", "toggleNavbarOnly"]),
        ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),

        changeType(type) {
            this.typeSelected = type;
            this.timeSelect = {
                year: (new Date()).getFullYear(),
                month: (new Date()).getMonth()
            }
        },

        async getMyTransactions(type, paramsFilter = {}) {
            this.isLoadDone = false;
            this.loadFailed = false;
            this.listTransaction = []
            this.showLoader();
            await MemberService.getMyTransactions(type, this.timeSelect, paramsFilter).then(
                (response) => {
                    this.hideLoader();
                    // MBR122: a failed result has no data — reading it threw and left the page blank
                    if (!response.data.result.isSuccess) {
                        this.loadFailed = true;
                        return;
                    }
                    this.listTransaction = response.data.data.list_transaction
                    this.totalLifetimeEarning = response.data.data.total_lifetime_earning ? response.data.data.total_lifetime_earning : "$ 0"
                    this.membershipCashback = response.data.data.membership_cashback ? response.data.data.membership_cashback : "$ 0"
                    this.membershipPoint = response.data.data.membership_point ? response.data.data.membership_point : "$ 0"
                    this.totalPendingEarnedAmount = response.data.data.total_pending_earned_amount ? response.data.data.total_pending_earned_amount : "$ 0"
                    this.totalCashbackUserd = response.data.data.total_cashback_userd ? response.data.data.total_cashback_userd : "$ 0"
                    this.totalDonation = response.data.data.total_donation ? response.data.data.total_donation : "$ 0"
                    this.currentTime = response.data.data.current_year_month
                    this.merchantMember = response.data.data.merchant_member
                    this.pointName = response.data.data.point_name
                    this.isLoadDone = true;
                    this.isFirstLoadDone = true;
                },
                (error) => {
                    this.hideLoader();
                    console.log(error);
                    this.loadFailed = true;
                }
            );
        },

        // same params the timeSelect watcher sends
        reloadTransactions() {
            if (this.isFilterTab) {
                this.getMyTransactions(this.typeSelected, this.params);
            } else {
                this.getMyTransactions(this.typeSelected);
            }
        },

        closeSnackbar() {
            this.snackbar = null;
        },

        filterCashbackType(idStatus) {
            this.filterArrCashbackEarnStatus = idStatus
        },

        filterCashbackTime(value) {
            this.filterCashbackTimeSelected = value
        },

        filterStore(value) {
            const index = this.filterArrStore.indexOf(value);
            if (index > -1) {
                this.filterArrStore.splice(index, 1);
            } else {
                this.filterArrStore.push(value);
            }
        },

        filterData() {
            $(".btn-close").click();
            $(".x-close").click();
            this.params.filter_cashback_earn_status = this.filterArrCashbackEarnStatus
            this.params.filter_store = this.filterArrStore
            this.params.filter_cashback_time = this.filterCashbackTimeSelected
            this.getMyTransactions(this.typeSelected, this.params);
        },
        resetData() {
            // Receipt's sheet only shows stores; leave the Wallet-only filters alone
            if (!this.isReceiptTab) {
                this.filterArrCashbackEarnStatus = [];
                this.filterCashbackTimeSelected = null;
            }
            this.filterArrStore = [];
            this.filterData()
        },

        // "14/09/2026" -> "14 Sep 2026"
        dayLabel(date) {
            const [day, month, year] = String(date).split("/");
            const name = MONTHS[Number(month) - 1];
            if (!isEnglish()) {
                return day && name && year
                    ? formatLocaleDate(new Date(year, Number(month) - 1, day), { day: "numeric", month: "short", year: "numeric" })
                    : "";
            }
            return day && name && year ? `${day} ${name.slice(0, 3)} ${year}` : "";
        },

        // "14/09/2026 03:24 PM" -> "03:24 PM"
        timeOf(createdAt) {
            return String(createdAt || "").split(" ").slice(1).join(" ");
        },

        // icon medallion: money in, voucher exchange (cash or points), money out
        rowKind(row) {
            if ([3, 12].includes(Number(row.transaction_type)) && this.typeSelected != 3) {
                return "voucher";
            }
            return row.transaction_type_object && row.transaction_type_object.SIGN == "+" ? "in" : "out";
        },

        format(date) {
            const getMonth = String(date.getMonth() + 1)
            const month = getMonth.padStart(2, '0')
            const year = String(date.getFullYear())
            return `${month}-${year}`
        },

        showLoader() {
            this.loaderActive = true;
        },
        hideLoader() {
            this.loaderActive = false;
        },
    },
    watch: {
        timeSelect(val) {
            if (val) {
                if (this.isFilterTab) {
                    this.getMyTransactions(this.typeSelected, this.params);
                } else {
                    this.getMyTransactions(this.typeSelected);
                }
            }
        }
    }
};
</script>
<style scoped>
/* MBR048 header + tabs */

/* Material Dashboard's `.page-header { overflow: hidden }` would stop the header sticking */
.mbr-activity.page-header {
    overflow: visible;
    overflow-x: clip;
    /* room for the fixed MbrBottomNav */
    padding-bottom: 120px;
    font-family: var(--mbr-font-body);
    color: var(--mbr-color-ink-headline);
}

/* custom.css still carries the legacy `* { font-family: Poppins }`;
   :where() keeps this at one attribute's specificity so the class rules below win */
.mbr-activity :where(:not(.material-icons-round)) {
    font-family: var(--mbr-font-body);
}

.mbr-activity .mask {
    background: var(--mbr-color-canvas) !important;
    opacity: 1;
}

/* mobile column, like Home and Rewards */
.mbr-activity .container {
    max-width: 440px;
    margin: 0 auto;
}

.mbr-activity__bar {
    position: sticky;
    top: 0;
    z-index: 20;
    padding: 32px 20px 12px;
    background: rgba(var(--mbr-color-canvas-rgb), 0.95);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
}

.mbr-activity__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 40px;
    margin-bottom: 16px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-activity__title {
    margin: 0;
    font-family: var(--mbr-font-headline);
    font-size: 28px;
    font-weight: 800;
    line-height: 36px;
    letter-spacing: -0.02em;
    color: var(--mbr-color-ink-strong);
}

.mbr-activity__segment {
    display: flex;
    padding: 4px;
    background: var(--mbr-color-surface);
    border: 1px solid var(--mbr-color-outline);
    border-radius: var(--mbr-radius-full);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-activity__seg {
    flex: 1 1 0;
    min-width: 0;
    padding: 8px 4px;
    background: none;
    border: 0;
    border-radius: var(--mbr-radius-full);
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--mbr-color-ink-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: background 0.15s ease, color 0.15s ease;
}

.mbr-activity__seg--active {
    background: var(--mbr-color-primary);
    font-weight: 700;
    color: var(--mbr-color-on-primary);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.12);
}

.mbr-activity__seg:focus-visible {
    outline: 2px solid var(--mbr-color-primary);
    outline-offset: 2px;
}
/* MBR049 summary card */
.mbr-activity__summary {
    position: relative;
    overflow: hidden;
    margin-bottom: 16px;
    padding: 20px;
    background: var(--mbr-color-primary);
    border-radius: var(--mbr-radius-xl);
    color: var(--mbr-color-on-primary);
    box-shadow: 0 10px 15px -3px rgba(var(--mbr-color-primary-rgb), 0.2), 0 4px 6px -4px rgba(var(--mbr-color-primary-rgb), 0.2);
}

.mbr-activity__ring {
    position: absolute;
    border: 1px solid rgba(var(--mbr-color-surface-rgb), 0.1);
    border-radius: var(--mbr-radius-full);
    pointer-events: none;
}

.mbr-activity__ring--a {
    right: -32px;
    bottom: -32px;
    width: 144px;
    height: 144px;
}

.mbr-activity__ring--b {
    right: 24px;
    bottom: -56px;
    width: 176px;
    height: 176px;
    border-color: rgba(var(--mbr-color-surface-rgb), 0.05);
}

.mbr-activity__sum-row {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.mbr-activity__sum-main {
    min-width: 0;
}

.mbr-activity__sum-side {
    flex: none;
    text-align: right;
}

.mbr-activity__eyebrow {
    display: block;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(var(--mbr-color-surface-rgb), 0.6);
}

.mbr-activity__eyebrow--peach {
    color: var(--mbr-color-peach);
}

.mbr-activity__eyebrow--gap {
    margin-top: 8px;
}

.mbr-activity__sum-value {
    margin-top: 2px;
    font-family: var(--mbr-font-headline);
    font-size: 26px;
    font-weight: 800;
    line-height: 32px;
    letter-spacing: -0.02em;
    color: var(--mbr-color-on-primary);
    overflow-wrap: anywhere;
}

.mbr-activity__sum-small {
    margin-top: 2px;
    font-family: var(--mbr-font-headline);
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: rgba(var(--mbr-color-surface-rgb), 0.8);
    white-space: nowrap;
}

.mbr-activity__lifetime {
    position: relative;
    margin-top: 10px;
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    color: rgba(var(--mbr-color-surface-rgb), 0.7);
}

.mbr-activity__lifetime strong {
    font-weight: 700;
    color: var(--mbr-color-on-primary);
}

.mbr-activity__notices {
    position: relative;
    margin: 12px 0 0;
    padding: 12px 0 0;
    list-style: none;
    border-top: 1px solid rgba(var(--mbr-color-surface-rgb), 0.15);
}

.mbr-activity__notices li {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    color: var(--mbr-color-peach);
}

.mbr-activity__notices li + li {
    margin-top: 4px;
}

.mbr-activity__notices svg {
    flex: none;
    width: 14px;
    height: 14px;
    margin-top: 1px;
}

/* the backend wraps the day count in <span style="color:#f00">, unreadable on maroon */
.mbr-activity__notices :deep(span[style]) {
    font-weight: 700;
    color: var(--mbr-color-on-primary) !important;
}
/* MBR050 tier / donation cards */
.mbr-activity__card {
    margin-bottom: 16px;
    padding: 18px 20px;
    background: var(--mbr-color-card);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
    border-radius: var(--mbr-radius-xl);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-activity__card-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.mbr-activity__card-main {
    min-width: 0;
}

.mbr-activity__label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--mbr-color-ink-secondary);
}

.mbr-activity__card-value {
    margin-top: 2px;
    font-family: var(--mbr-font-headline);
    font-size: 22px;
    font-weight: 800;
    line-height: 28px;
    letter-spacing: -0.015em;
    color: var(--mbr-color-primary);
    overflow-wrap: anywhere;
}

.mbr-activity__tier {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
    max-width: 50%;
}

.mbr-activity__tier-icon {
    width: 36px;
    height: 36px;
    object-fit: contain;
    border-radius: var(--mbr-radius-full);
}

.mbr-activity__tier-name {
    padding: 4px 10px;
    background: var(--mbr-color-peach);
    border-radius: var(--mbr-radius-full);
    font-family: var(--mbr-font-headline);
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: var(--mbr-color-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mbr-activity__progress {
    margin-top: 14px;
}

.mbr-activity__note {
    margin: 10px 0 0;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    color: var(--mbr-color-ink-secondary);
}
/* MBR051 month picker + rows */
.mbr-activity__body {
    padding: 8px 20px 0;
}

.mbr-activity__month-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-top: 4px;
}

.mbr-activity__picker {
    width: auto;
}

.mbr-activity__month {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    background: var(--mbr-color-tint);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.7);
    border-radius: var(--mbr-radius-full);
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: var(--mbr-color-primary);
}

.mbr-activity__month svg {
    width: 14px;
    height: 14px;
}

.mbr-activity__month:focus-visible {
    outline: 2px solid var(--mbr-color-primary);
    outline-offset: 2px;
}

.mbr-activity__count {
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.7);
}

.mbr-activity__group {
    margin-top: 16px;
}

/* a div-sized label: custom.css forces mobile h2 to 22px !important */
.mbr-activity .mbr-activity__day {
    margin: 0 0 10px;
    font-family: var(--mbr-font-body);
    font-size: 11px !important;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--mbr-color-ink-secondary);
}

.mbr-activity__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 14px;
    background: var(--mbr-color-card);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
    border-radius: var(--mbr-radius-lg);
    box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-activity__row + .mbr-activity__row {
    margin-top: 10px;
}

.mbr-activity__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    width: 44px;
    height: 44px;
    border-radius: var(--mbr-radius-full);
    color: var(--mbr-color-primary);
}

.mbr-activity__icon svg {
    width: 20px;
    height: 20px;
}

.mbr-activity__icon--in {
    background: var(--mbr-color-peach);
}

.mbr-activity__icon--out {
    background: var(--mbr-color-palepink);
}

.mbr-activity__icon--voucher {
    background: var(--mbr-color-tint);
    border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
}

/* basis 0: with flex-wrap on the row, an auto basis pushed the amount onto its own line */
.mbr-activity__row-main {
    flex: 1 1 0%;
    min-width: 0;
}

.mbr-activity__row-title {
    font-family: var(--mbr-font-headline);
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: var(--mbr-color-ink-strong);
    overflow-wrap: anywhere;
}

.mbr-activity__row-time {
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.7);
}

.mbr-activity__row-meta {
    margin-top: 2px;
    font-size: 10px;
    line-height: 14px;
    color: rgba(var(--mbr-color-ink-secondary-rgb), 0.6);
    overflow-wrap: anywhere;
}

/* full width under the icon/label/amount line, indented past the icon */
.mbr-activity__row-earn {
    flex: 1 0 calc(100% - 56px);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin: -4px 0 0 56px;
    padding-top: 8px;
    border-top: 1px dashed rgba(var(--mbr-color-outline-rgb), 0.7);
    font-size: 11px;
    font-weight: 500;
    line-height: 16px;
    color: var(--mbr-color-ink-secondary);
}

.mbr-activity__status {
    flex: none;
    padding: 2px 8px;
    border-radius: var(--mbr-radius-full);
    font-size: 10px;
    font-weight: 700;
    line-height: 14px;
}

.mbr-activity__status--ok {
    background: rgba(var(--mbr-color-positive-rgb), 0.1);
    color: var(--mbr-color-positive);
}

.mbr-activity__status--wait {
    background: var(--mbr-color-peach);
    color: var(--mbr-color-primary);
}

.mbr-activity__amount {
    flex: none;
    align-self: flex-start;
    padding-top: 2px;
    font-family: var(--mbr-font-headline);
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    text-align: right;
    white-space: nowrap;
}

.mbr-activity__amount--in {
    color: var(--mbr-color-positive);
}

.mbr-activity__amount--out {
    color: var(--mbr-color-primary);
}

.mbr-activity__empty {
    margin: 32px 0 0;
    font-size: 13px;
    font-weight: 500;
    text-align: center;
    color: var(--mbr-color-ink-muted);
}

/* MBR122 load error — same look as the Home (MBR121) error state */
.mbr-activity__error {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 16px 24px;
    text-align: center;
}

.mbr-activity__error--page {
    justify-content: center;
    min-height: calc(100vh - 200px);
}

.mbr-activity__error-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 8px;
    background: var(--mbr-color-tint);
    border-radius: var(--mbr-radius-full);
    color: var(--mbr-color-primary);
}

.mbr-activity__error-icon .material-icons-round {
    font-size: 32px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-activity__error-title {
    margin: 0;
    font-family: var(--mbr-font-headline);
    font-size: var(--mbr-headline-sm-size);
    font-weight: 700;
    line-height: var(--mbr-headline-sm-line);
    color: var(--mbr-color-ink-headline);
}

.mbr-activity__error-sub {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: var(--mbr-color-ink-muted);
}

.mbr-activity__error-retry {
    min-width: 160px;
    margin-top: 12px;
}
</style>
